import { Component, h, Element, Listen, Prop, State } from '@stencil/core';
import { isOnScreen, guid, _BREAKPOINTS_ } from 'utils';
import Glide from '@glidejs/glide';

@Component({
	tag: 'kdx-carousel',
	styleUrl: 'kdx-carousel.scss'
})
export class StCarousel {

	@Element() root: HTMLElement;

    /**
     * glideJS type.  Caorusel (seemless) or slider (rewinds)
     */
    @Prop() carouselType: string = "slider";

    /**
     * Display flag for showing the bullet navigation. Pass true to show.
     */
    @Prop() bulletNav: boolean;

    /* On multi item slides, set focus to middle item.
     */
    @Prop() centerFocus: boolean;
    @Prop() centerFocusMobile: boolean = false;

    /*
     * arrows Icons
     */
    @Prop() hideArrows: boolean = false;
    @Prop() leftArrowIcon: string = 'chevron-left';
    @Prop() leftArrowIconMobile: string = 'chevron-left';
    @Prop() rightArrowIcon: string = 'chevron-right';
    @Prop() rightArrowIconMobile: string = 'chevron-right';
    
    /**
     * If true, bullet nav will display below slides. Default will show bullet
     * nav within slides area.
     */
    @Prop() displayBulletNavExternally: boolean;

    /**
     * Amount of items to display per slide. Default will show 1 item per slide.
     */
    @Prop() perSlide: string = "1";
    @Prop() perSlideTablet: string = "1";
    @Prop() perSlideMobile: string = "1";

    /**
     * shows some of next card
     */
    @Prop() peek: number = 0; // use to add peek anytime
    @Prop() peekTablet: number = 0; // show peek on tablet
    @Prop() peekMobile: number = 0; // show peek on mobile

    /**
     * If set, slides will autoplay and a play/pause button will appear.
     */
    @Prop() autoPlay: boolean;

    /** 
     * If set, the pause/play icon will also have text next to it.  ex:   || Pause 
     */
    @Prop() pauseText: string;
    @Prop() playText: string;

    /**
     * Override the default duration for slide times while on autoplay.
     */
    @Prop() autoPlayDuration: string = '8000';

    /**
     * Provide styling and support for the slider showing cards.
     */
    @Prop() cardsSlider: boolean;

    /**
     * Provide styling if carousel goes full width with no container
     */
    @Prop() fullWidth: boolean;

    /** 
     * default gap between items
     */
    @Prop() gap: number = 32;
    @Prop() gapTablet: number = 32;
    @Prop() gapMobile: number = 16;

    /**
     * Additional CSS classes on section tag
     */
    @Prop() cssClasses: string;

    /**
     * Adjustable Height - when set, this will resize slider to the height of active slide
     */    
    @Prop() adjustableHeightOnMobile: boolean;

    /**
     * Controls paused/autoplay state of carousel.
     */
    @State() paused: boolean = false;

    /**
     * Determins which left/right arrows to use based off screen
     */
    @State() leftArrow: string;
    @State() rightArrow: string;

    @State() notEnoughItemsForArrows: boolean = false;

    private id: string = 'kdx-carousel-' + guid();
    private initialRenderComplete = false;
    private imagesLazyLoaded = false;
    private carousel: any = null;
    private slideCount = 0;

    componentDidRender() {
        if (document.querySelector('.is-editing.sfPageEditor')) {
            return;
        }

        const instance = document.getElementById(this.id);

        if (!this.initialRenderComplete) {
            this.setUp(instance);
            const slides = instance.querySelector('.glide__slides');

            if (slides) {
                slides.setAttribute('id', this.id + '-slides');
            }
        }

        if (isOnScreen(instance)) {
            this.renderLazyImages(instance);
        }

        window.addEventListener('scroll', () => {
            if (isOnScreen(instance)) {
                this.renderLazyImages(instance);
            }
        });
    }

    hideOrShowArrows() {
        let totalSlides = this.root.querySelector('.glide__slides').children.length;
        this.slideCount = totalSlides;

        if (window.innerWidth >= 981) {

            this.notEnoughItemsForArrows = totalSlides <= parseInt(this.perSlide, 10) ? true : false;
        }

        if (window.innerWidth <= 980 && window.innerWidth >= 721) {
            this.notEnoughItemsForArrows = totalSlides <= parseInt(this.perSlideTablet, 10) ? true : false;
        }

        if (window.innerWidth <= 720) {
            this.notEnoughItemsForArrows = totalSlides <= parseInt(this.perSlideMobile, 10) ? true : false;
        }
    }

    componentWillLoad() {
        this.leftArrow = window.innerWidth > _BREAKPOINTS_.sm ? this.leftArrowIcon : this.leftArrowIconMobile;
        this.rightArrow = window.innerWidth > _BREAKPOINTS_.sm ? this.rightArrowIcon : this.rightArrowIconMobile;

        this.hideOrShowArrows();
    }

    componentDidLoad() {
        // Has difficulty finding lazy loaded bg images. re initialize lazy load observer
        globalThis.imageLoader?.initialize(); 
        this.hideOrShowArrows();       
    }

    @Listen('resize', { target: 'window' })
    resizeHandler() {
        this.leftArrow = window.innerWidth > _BREAKPOINTS_.sm ? this.leftArrowIcon : this.leftArrowIconMobile;
        this.rightArrow = window.innerWidth > _BREAKPOINTS_.sm ? this.rightArrowIcon : this.rightArrowIconMobile;

        this.hideOrShowArrows();
    }

    private setUp(instance) {
        const bulletsWrapper = instance.querySelector('.glide__bullets');

        if (bulletsWrapper) {
            for (let i = 0; i < this.slideCount; i++) {
                bulletsWrapper.innerHTML += `<button class="glide__bullet" data-glide-dir="=${i}" aria-label="Go to slide ${i + 1}" aria-controls=${this.id + '-slides'}></button>`;
            }
        }

        this.autoPlay = !this.notEnoughItemsForArrows;

        if (this.perSlide) {
            this.carousel = new Glide(`#${this.id}`, {
                type: this.carouselType,
                perView: parseInt(this.perSlide, 10),
                perSwipe: '|',
                focusAt: this.centerFocus ? 'center' : '',
                autoplay: this.autoPlay ? parseInt(this.autoPlayDuration, 10) : null,
                hoverpause: this.notEnoughItemsForArrows,
                peek: this.peek,
                gap: this.gap,
                breakpoints: {
                    980: {
                        perView: parseInt(this.perSlideTablet, 10),
                        peek: this.peekTablet,
                        gap: this.gapTablet
                    },
                    720: {
                        perView: parseInt(this.perSlideMobile, 10),
                        peek: this.peekMobile,
                        gap: this.gapMobile,
                        focusAt: this.centerFocusMobile ? 'center' : '',
                    },
                },
            }).mount();
        } else {
            this.carousel = new Glide(`#${this.id}`, {
                autoplay: this.autoPlay ? parseInt(this.autoPlayDuration, 10) : null,
                hoverpause: false,
            }).mount();
        }
        if (this.notEnoughItemsForArrows) {
          var styles = "kdx-logo-carousel .slide-item.glide__slide--clone, kdx-carousel .glide__slide--clone { visibility: hidden; }";
          var styleSheet = document.createElement("style");
          styleSheet.textContent = styles;
          document.head.appendChild(styleSheet);
          this.hideArrows = true;
        }
        this.carousel.on('move.after', () => {

            if (this.paused) {
                this.carousel.pause();
                setTimeout(() => {
                    this.carousel.pause();
                }, 1000);
            } else {
                this.carousel.play();
            }
        });  


        if (this.cardsSlider) {
            this.setCountText();

            this.carousel.on(['run.after', 'resize'], () => {
                this.setCountText();
            });
        }

        // adjustable height.  Change height to active slide height, and scrolls to top of slide.  good for mobile with variable height slides
        if (this.adjustableHeightOnMobile && window.innerWidth <= _BREAKPOINTS_['md']) {

            this.carousel.on('build.after', ()=> {
                let slideHeight = this.root.querySelector('.glide__slide--active .row').clientHeight;
                let glideTrack = this.root.querySelector('.glide__track').clientHeight;
                
                if (slideHeight != glideTrack) {
                    let newHeight = slideHeight;
                    (this.root.querySelector('.glide__track') as HTMLElement).style.height = newHeight+'px';
                    this.root.querySelector('.glide__track').scrollIntoView({behavior: "smooth", block: "start"});
                }
            });
            
            this.carousel.on('run.after', ()=> {
                var slideHeight = this.root.querySelector('.glide__slide--active .row').clientHeight;
                var glideTrack = this.root.querySelector('.glide__track').clientHeight;
                
                if (slideHeight != glideTrack) {
                    var newHeight = slideHeight;
                    (this.root.querySelector('.glide__track') as HTMLElement).style.height = newHeight+'px';
                    this.root.querySelector('.glide__track').scrollIntoView({behavior: "smooth", block: "start"});
                }
            });
        }

        this.carousel.on('mount.after', () => {
            globalThis.imageLoader?.initialize();            
        });

        this.initialRenderComplete = true;
    }

    setCountText() {
        const instance = document.getElementById(this.id);
        const countText = instance.querySelector('.st-carousel__showing') as HTMLElement;

        const start = this.carousel.index;
        const perView = this.carousel.settings.perView;
        const max = this.slideCount;

        const low = start + 1;
        const heigh = Math.min(start + perView, max);

        if (low != heigh) {
            countText.innerText = `${low}-${heigh} of ${this.slideCount}`;
        } else {
            countText.innerText = `${low} of ${this.slideCount}`;
        }
    }

    private togglePause(e: Event) {
        e.preventDefault();
        this.paused = !this.paused;

        if (this.paused) {
            this.carousel.pause();
        } else {
            this.carousel.play();
        }
    }

    /**
     * Load any lazy images within carousel. Avoids images loading while slides transition.
     */
    renderLazyImages(instance: HTMLElement) {
        if (this.imagesLazyLoaded) {
            return;
        }

        const images = instance.querySelectorAll('.img-load-lazy');

        images.forEach(lazyImage => {
            const src = lazyImage.getAttribute('data-src');
            const srcSet = lazyImage.getAttribute('data-srcset');

            if (src) {
            }
            if (srcSet) {
                lazyImage.setAttribute('srcset', srcSet);
            }
            lazyImage.classList.remove('img-load-lazy');
        });

        this.imagesLazyLoaded = true;
    }

    render() {
        return (
            <div 
                id={this.id} 
                class={`glide kdx-carousel 
                        ${this.cssClasses} 
                        ${this.cardsSlider && '-cards-slider'} 
                        ${this.notEnoughItemsForArrows ? "only-1-item" : ""}
                        ${this.fullWidth ? "full-width" : ""} 
                `}>

                <div class="st-carousel-inner">
                    <div class="glide__track" data-glide-el="track">
                        <slot />
                        {!this.hideArrows && !this.notEnoughItemsForArrows && (
                        <div data-glide-el="controls" class="controls">
                            <button data-glide-dir="<" class="glide__btns left-btn" aria-label="Previous Slide" aria-controls={this.id + '-slides'}>
                                <kdx-icon icon={this.leftArrow} />
                            </button>
                            <button data-glide-dir=">" class="glide__btns right-btn" aria-label="Next Slide" aria-controls={this.id + '-slides'}>
                                <kdx-icon icon={this.rightArrow} />
                            </button>
                        </div>
                    )}
                    </div>
                </div>

                {this.cardsSlider && <div class="st-carousel__showing"></div>}

                {this.bulletNav && !this.notEnoughItemsForArrows && (
                    <div class={`bullets-wrapper ${this.displayBulletNavExternally ? 'external' : 'internal'}`}>
                        <div class="glide__bullets" data-glide-el="controls[nav]"></div>
                        {this.autoPlay && (
                            <div class="pause-action-wrapper">
                                <button
                                    class="pause-action"
                                    aria-label={this.paused ? 'Play' : 'Pause'}
                                    aria-controls={this.id + '-slides'}
                                    onClick={(e) => {
                                        this.togglePause(e);
                                    }}
                                >
                                    {
                                        this.paused && 
                                        <span class="play-icon">
                                            <kdx-icon icon="play" />
                                            {this.playText && <span class="text">{this.playText}</span>}
                                        </span>
                                    }
                                    {!this.paused && 
                                        <span class="pause-icon">
                                            <kdx-icon icon="pause" />
                                            {this.pauseText && <span class="text">{this.pauseText}</span>}
                                        </span>
                                    }
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    }

}
